import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import InfoBox from "../info-box/InfoBox";
import { CustomButton, CustomDialog, CustomIconButton, CustomTooltip, icon } from "../../../../../common/components";
import { hasCompanyPermission, hasPermission, Permissions, translate } from "../../../../../common/providers";
import { Summary, EmptyState } from "./component";
import styles from "./ProjectSummary.module.css";

const ProjectSummary = ({ className, summary = null, isGenerating = false, onGenerate, onUpdate }) => {
	const hasAvailableAIQuota = useSelector(({ context }) => context?.hasAvailableAIQuota);
	const [isEditing, setIsEditing] = useState(false);
	const [openWarningPopup, setOpenWarningPopUp] = useState(false);
	const hasActionPermission = useMemo(
		() => hasPermission(Permissions.PROJECT_LEADER) && hasCompanyPermission(Permissions.PROJECT_LEADER),
		[]
	);
	const handleStartEditing = () => {
		setIsEditing(true);
	};
	const handleSaveEditing = (value) => {
		setIsEditing(false);
		onUpdate(value);
	};
	const handleOpenWarningPopUp = () => {
		setOpenWarningPopUp(true);
	};
	const handleCloseWarningPopUp = () => {
		setOpenWarningPopUp(false);
	};
	const handleConfirm = () => {
		onGenerate();
		handleCloseWarningPopUp();
	};
	const handleCancel = () => {
		handleCloseWarningPopUp();
	};
	return (
		<InfoBox
			actions={
				summary &&
				!isGenerating &&
				hasActionPermission && (
					<div className={styles.actions}>
						<CustomButton
							color="secondary"
							disabled={isEditing}
							size="sm"
							startIcon={icon.faPen}
							variant="outlined"
							onClick={handleStartEditing}
						>
							{translate("common:btn.edit")}
						</CustomButton>
						<CustomTooltip
							title={
								(!hasAvailableAIQuota &&
									translate("common:company.quotas.consumption-reached.tooltip")) ||
								""
							}
						>
							<span>
								<CustomIconButton
									color="secondary"
									disabled={!hasAvailableAIQuota}
									icon={icon.faRotateRight}
									iconColor={!hasAvailableAIQuota && "var(--color-light-grey-2)"}
									size="xs"
									tooltip={translate("common:btn.relaunch")}
									variant="outlined"
									onClick={handleOpenWarningPopUp}
								/>
							</span>
						</CustomTooltip>
					</div>
				)
			}
			className={className}
			icon={icon.faHammer}
			title={translate("dashboards.project.project-summary.title")}
		>
			<div className={styles.projectSummary}>
				{(!!summary && !isGenerating && (
					<Summary isEditing={isEditing} summary={summary} onSaveEditing={handleSaveEditing} />
				)) || (
					<EmptyState
						hasActionPermission={hasActionPermission}
						isGenerating={isGenerating}
						onClick={onGenerate}
					/>
				)}
				<CustomDialog
					iconColor="var(--blue-500)"
					iconModel={icon.faInfoCircle}
					open={openWarningPopup}
					submitLabel={translate("common:btn.confirm")}
					title={translate("dashboards.project.project-summary.warning-dialog-title")}
					onClose={handleCancel}
					onSubmit={handleConfirm}
				>
					<div>
						<div className={styles.warningSubtitle}>
							{translate("dashboards.project.project-summary.warning-dialog-sub-title")}
						</div>
						<div>{translate("dashboards.project.project-summary.warning-dialog-sub-title.continue")}</div>
					</div>
				</CustomDialog>
			</div>
		</InfoBox>
	);
};

export default ProjectSummary;
